import React from "react";
import "./AlbumCard.css";

/**
 * AlbumCard
 * Used for: latest release (Home), releases carousel, shop grid, and the
 * Music page's featured-release strip.
 *
 * Props:
 *  - title, artist, format   e.g. "Frequencies", "Akwerius", "Vinyl · LP"
 *  - coverSrc                optional image; falls back to a gradient block
 *  - badge                   e.g. "New" (pill in top corner) — "card" layout only
 *  - price                   e.g. "$28" — omit to hide the price row
 *  - remaining / total       optional pre-order progress ("27 of 100 remaining")
 *  - ctaLabel / onCtaClick   e.g. "Pre-order"
 *  - secondaryCtaLabel / onSecondaryCtaClick   e.g. "Stream" — "featured" layout only
 *  - compact                 tighter layout for sidebar/carousel use — "card" layout only
 *  - layout                  "card" (default) | "featured" (wide strip with
 *                             inventory % on the right, per the Music page wireframe)
 */
export default function AlbumCard({
  title = "Untitled release",
  artist = "MIHU Collective",
  format = "Vinyl · LP",
  coverSrc,
  badge,
  price,
  remaining,
  total,
  ctaLabel = "Pre-order",
  onCtaClick,
  secondaryCtaLabel,
  onSecondaryCtaClick,
  compact = false,
  layout = "card",
}) {
  const pct =
    remaining != null && total ? Math.round(((total - remaining) / total) * 100) : null;

  if (layout === "featured") {
    return (
      <div className="album-card-featured">
        <div className="album-card-featured-art">
          {coverSrc ? (
            <img src={coverSrc} alt={`${title} cover`} />
          ) : (
            <span aria-hidden="true">♪</span>
          )}
        </div>
        <div className="album-card-featured-body">
          <div className="album-card-featured-title">{title}</div>
          <div className="album-card-featured-meta">{artist} · {format}</div>
          <div className="album-card-featured-ctas">
            {ctaLabel && (
              <button className="album-card-cta album-card-featured-cta" onClick={onCtaClick}>
                {ctaLabel}{price ? ` · ${price}` : ""}
              </button>
            )}
            {secondaryCtaLabel && (
              <button className="album-card-featured-secondary-cta" onClick={onSecondaryCtaClick}>
                {secondaryCtaLabel}
              </button>
            )}
          </div>
        </div>
        {pct != null && (
          <div className="album-card-featured-inventory">
            <div className="album-card-featured-inventory-label">Inventory</div>
            <div className="album-card-featured-inventory-pct">{pct}%</div>
            <div className="album-card-featured-inventory-label">remaining</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={"album-card" + (compact ? " compact" : "")}>
      <div className="album-card-art">
        {coverSrc ? (
          <img src={coverSrc} alt={`${title} cover`} />
        ) : (
          <span className="album-card-art-placeholder" aria-hidden="true">♪</span>
        )}
        {badge && <span className="album-card-badge">{badge}</span>}
      </div>

      <div className="album-card-body">
        <div className="album-card-row">
          <span className="album-card-title">{title}</span>
          {price && <span className="album-card-price">{price}</span>}
        </div>
        <div className="album-card-meta">{artist} · {format}</div>

        {pct != null && (
          <>
            <div className="album-card-progress">
              <div className="album-card-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="album-card-remaining">{remaining} of {total} remaining</div>
          </>
        )}

        {ctaLabel && (
          <button className="album-card-cta" onClick={onCtaClick}>
            {ctaLabel}
          </button>
        )}
      </div>
    </div>
  );
}
